import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, AlertTriangle} from 'lucide-react';
import Webcam from 'react-webcam';
import { exerciseAPI, recordsAPI } from '../services/api';
import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const ExercisePage = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastAnalysisTime = useRef(0);
  const canvasDimensions = useRef({ width: 640, height: 480 });
  const lastTimestampRef = useRef(-1);
  
  // 반복 카운팅을 위한 상태
  const lastRepScore = useRef(0);
  const repCooldown = useRef(false);
  const scoreHistory = useRef([]); // ✅ 점수 히스토리 추적
  const isInDownPhase = useRef(false); // ✅ 동작 중 플래그 (하강 중)
  
  // ✅ 정적 운동 (자세 유지) 카운팅
  const holdStartTime = useRef(null);
  const currentHoldScore = useRef([]);
  
  // ✅ 마지막으로 감지된 포즈 저장 (깜빡임 방지)
  const lastValidPose = useRef(null);
  
  // ✅ 별도의 렌더링 루프를 위한 ref
  const renderLoopRef = useRef(null);
  
  // ✅ guideFrame을 ref로 관리 (렌더링 루프에서 실시간 참조)
  const guideFrameRef = useRef(0);

  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentSet, setCurrentSet] = useState(1);
  const [currentRep, setCurrentRep] = useState(0);
  const [feedback, setFeedback] = useState('준비하세요');
  const [score, setScore] = useState(100);
  const [totalScore, setTotalScore] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showGuide, setShowGuide] = useState(true);
  const [guideFrame, setGuideFrame] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [guidePoses, setGuidePoses] = useState([]);
  const [completionFeedback, setCompletionFeedback] = useState(null);
  const [isMediaPipeReady, setIsMediaPipeReady] = useState(false);
  const [poseDetected, setPoseDetected] = useState(false);
  const [isSavingCompletion, setIsSavingCompletion] = useState(false); // ✅ 저장 중 상태
  const [saveError, setSaveError] = useState(null); // ✅ 저장 에러

  // 운동 정보 불러오기
  useEffect(() => {
    const fetchExercise = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await exerciseAPI.getExercise(exerciseId);
        console.log('✅ Exercise data received:', response.data);
        
        setExercise(response.data);
        
        if (response.data.silhouette_animation?.keyframes) {
          const allKeyframes = response.data.silhouette_animation.keyframes;
          
          let selectedKeyframes = allKeyframes;
          if (allKeyframes.length > 15) {
            const step = Math.floor(allKeyframes.length / 15);
            selectedKeyframes = allKeyframes.filter((_, index) => index % step === 0);
            console.log(`⚠️ 키프레임 샘플링: ${allKeyframes.length} → ${selectedKeyframes.length}`);
          }
          
          console.log(`✅ 모든 키프레임 사용: ${allKeyframes.length}개`);

          const poses = allKeyframes.map(keyframe => {
            const poseObj = {};
            if (keyframe.pose_landmarks) {
              keyframe.pose_landmarks.forEach((landmark, index) => {
                poseObj[index.toString()] = landmark;
              });
            }
            return poseObj;
          });

          const validPoses = poses.filter(pose => {
            const requiredLandmarks = [0, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28, 31, 32];
            return requiredLandmarks.every(idx => pose[idx.toString()]);
          });

          if (validPoses.length < 2) {
            console.error('❌ 유효한 포즈가 부족합니다');
            setError('운동 애니메이션 데이터가 올바르지 않습니다');
            return;
          }

        setGuidePoses(validPoses);
          
          console.log('✅ Processed guide poses:', poses.length);
          setGuidePoses(poses);
        } else {
          console.log('⚠️ No silhouette_animation found');
          setGuidePoses([]);
        }
        
        setTimeRemaining(response.data.duration_seconds);
        setLoading(false);
      } catch (error) {
        console.error('❌ 운동 정보 로드 실패:', error);
        setError(error.response?.data?.message || error.message || '운동 정보를 불러올 수 없습니다');
        setLoading(false);
      }
    };

    if (exerciseId) {
      fetchExercise();
    }
  }, [exerciseId]);

  // 가이드 프레임 애니메이션
  useEffect(() => {
    if (!isStarted || isPaused || !showGuide || isCompleted || guidePoses.length === 0) {
      return;
    }

    console.log('🎬 가이드 애니메이션 시작:', guidePoses.length, '프레임');
    
    const frameInterval = 2000;
    console.log(`⏱️ 프레임 전환 간격: ${frameInterval}ms`);
    
    const interval = setInterval(() => {
      setGuideFrame(prev => {
        const nextFrame = (prev + 1) % guidePoses.length;
        console.log('🔄 프레임 전환:', prev, '→', nextFrame);
        // ✅ ref도 함께 업데이트
        guideFrameRef.current = nextFrame;
        return nextFrame;
      });
    }, frameInterval);

    return () => {
      console.log('⏹️ 가이드 애니메이션 정지');
      clearInterval(interval);
    };
  }, [isStarted, isPaused, showGuide, isCompleted, guidePoses.length]);

  // 완료 데이터 저장
  const saveCompletion = useCallback(async () => {
    console.log('💾 ========== 운동 완료 저장 시작 ==========');
    
    const avgScore = totalScore.length > 0 
      ? Math.round(totalScore.reduce((a, b) => a + b, 0) / totalScore.length)
      : 0;
    
    if (!exercise) {
      console.error('❌ exercise 정보 없음!');
      return;
    }
    
    if (!exerciseId) {
      console.error('❌ exerciseId 없음!');
      return;
    }

    setIsSavingCompletion(true);
    setSaveError(null);

    try {
      // ✅ 백엔드 스키마에 맞게 데이터 구성
      const recordData = {
        exercise_id: exerciseId,
        duration_minutes: Math.max(1, Math.ceil((exercise.duration_seconds - timeRemaining) / 60)),
        completed_sets: currentSet,
        completed_reps: currentRep,
        average_score: avgScore,
        pain_level_before: 5,
        pain_level_after: 5,
        feedback: null,
        pose_analysis_summary: totalScore.length > 0 ? {
          total_measurements: totalScore.length,
          score_history: totalScore,
          average_score: avgScore,
          min_score: Math.min(...totalScore),
          max_score: Math.max(...totalScore)
        } : null
      };
      
      console.log('📤 전송 데이터:', recordData);
      
      // ✅ API URL 최종 확인
      console.log('🌐 API 호출 정보:');
      console.log('   - 엔드포인트: POST /records');
      console.log('   - 예상 URL:', window.location.hostname === 'localhost' 
        ? 'http://localhost:8000/api/v1/records'
        : 'https://fitner-api-697550966480.asia-northeast3.run.app/api/v1/records'
      );
      
      // ✅ recordsAPI 사용 (exerciseAPI 대신)
      const response = await recordsAPI.createRecord(recordData);
      
      console.log('✅ 운동 완료 저장 성공!');
      console.log('📥 응답 데이터:', response.data);
      
      if (response.data?.feedback) {
        setCompletionFeedback(response.data.feedback);
        console.log('💬 피드백 수신:', response.data.feedback);
      }
      
      setIsSavingCompletion(false);
      console.log('💾 ========== 저장 완료 ==========');
      
    } catch (error) {
      console.error('❌ ========== 완료 저장 실패 ==========');
      console.error('에러 상세:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        exerciseId: exerciseId,
        url: error.config?.url,
        method: error.config?.method
      });
      
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || '알 수 없는 오류';
      setSaveError(errorMsg);
      setIsSavingCompletion(false);
      
      // ✅ 사용자에게 에러 알림
      alert(`기록 저장 실패: ${errorMsg}\n\n완료 데이터:\n세트: ${currentSet}\n반복: ${currentRep}\n평균 점수: ${avgScore}\n\n❗ F12 콘솔을 확인해주세요`);
    }
  }, [totalScore, exercise, currentSet, currentRep, timeRemaining, exerciseId]);

  // ✅ 운동 타입 감지 (정적 vs 동적)
  const isStaticExercise = useCallback((exerciseName) => {
    if (!exerciseName) return false;
    
    const staticKeywords = [
      '플랭크', 'plank',
      '유지', 'hold',
      '버티기',
      '스트레칭', 'stretch', 'stretching',
      '자세 유지',
      '정적'
    ];
    
    const name = exerciseName.toLowerCase();
    return staticKeywords.some(keyword => name.includes(keyword));
  }, []);

  // 가이드 실루엣 그리기
  const drawGuideSilhouette = useCallback((ctx, guidePose, width, height) => {
  if (!guidePose) {
    console.warn('⚠️ guidePose가 없습니다');
    return;
  }
  
  // ✅ 필수 랜드마크 확인
  const requiredLandmarks = ["11", "12", "23", "24"];
  const hasRequired = requiredLandmarks.every(idx => guidePose[idx]);
  
  if (!hasRequired) {
    console.warn('⚠️ 필수 랜드마크 부족:', requiredLandmarks.filter(idx => !guidePose[idx]));
    return;
  }
  
  const shoulder_left = guidePose["11"];
  const shoulder_right = guidePose["12"];
  const hip_left = guidePose["23"];
  const hip_right = guidePose["24"];

  // 몸통 그리기 (더 선명하게)
  ctx.fillStyle = 'rgba(59, 130, 246, 0.4)';  // ✅ 투명도 증가
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';  // ✅ 테두리 진하게
  ctx.lineWidth = 4;  // ✅ 선 두껍게
  ctx.beginPath();
  ctx.moveTo(shoulder_left.x * width, shoulder_left.y * height);
  ctx.lineTo(shoulder_right.x * width, shoulder_right.y * height);
  ctx.lineTo(hip_right.x * width, hip_right.y * height);
  ctx.lineTo(hip_left.x * width, hip_left.y * height);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // 팔다리 그리기 (개선)
  const drawDetailedLimb = (joints) => {
    const hasAllJoints = joints.every(j => guidePose[j]);
    if (!hasAllJoints) {
      // 부분적으로라도 그리기
      const availableJoints = joints.filter(j => guidePose[j]);
      if (availableJoints.length >= 2) {
        ctx.lineWidth = 14;  // ✅ 더 굵게
        ctx.lineCap = 'round';
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.7)';
        ctx.beginPath();
        ctx.moveTo(guidePose[availableJoints[0]].x * width, guidePose[availableJoints[0]].y * height);
        for (let i = 1; i < availableJoints.length; i++) {
          ctx.lineTo(guidePose[availableJoints[i]].x * width, guidePose[availableJoints[i]].y * height);
        }
        ctx.stroke();
      }
      return;
    }

    // 완전한 팔다리
    ctx.lineWidth = 14;
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.7)';
    ctx.beginPath();
    ctx.moveTo(guidePose[joints[0]].x * width, guidePose[joints[0]].y * height);
    
    for (let i = 1; i < joints.length; i++) {
      ctx.lineTo(guidePose[joints[i]].x * width, guidePose[joints[i]].y * height);
    }
    ctx.stroke();

    // 관절 점 (더 크게)
    joints.forEach(jointIdx => {
      if (guidePose[jointIdx]) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
        ctx.beginPath();
        ctx.arc(
          guidePose[jointIdx].x * width, 
          guidePose[jointIdx].y * height, 
          8,  // ✅ 크기 증가
          0, 
          2 * Math.PI
        );
        ctx.fill();
      }
    });
  };

  // 팔다리 그리기
  drawDetailedLimb(["11", "13", "15", "19"]);  // 왼팔
  drawDetailedLimb(["12", "14", "16", "20"]);  // 오른팔
  drawDetailedLimb(["23", "25", "27", "31"]);  // 왼다리
  drawDetailedLimb(["24", "26", "28", "32"]);  // 오른다리

  // 머리 (더 크게)
  const neckX = (shoulder_left.x + shoulder_right.x) / 2;
  const neckY = (shoulder_left.y + shoulder_right.y) / 2;
  ctx.fillStyle = 'rgba(59, 130, 246, 0.6)';
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(neckX * width, (neckY - 0.08) * height, 25, 0, 2 * Math.PI);  // ✅ 크기 증가
  ctx.fill();
  ctx.stroke();
}, []);

  // ✅ 사용자 스켈레톤 그리기 (분리)
  const drawUserSkeleton = useCallback((ctx, poseLandmarks, width, height) => {
    if (!poseLandmarks || poseLandmarks.length === 0) return;

    const connections = [
      [11, 12], [11, 23], [12, 24], [23, 24],
      [11, 13], [13, 15], [15, 19],
      [12, 14], [14, 16], [16, 20],
      [23, 25], [25, 27], [27, 31],
      [24, 26], [26, 28], [28, 32]
    ];
    
    // 연결선 그리기 (밝은 초록색)
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 4;
    connections.forEach(([start, end]) => {
      const startPoint = poseLandmarks[start];
      const endPoint = poseLandmarks[end];
      if (startPoint && endPoint) {
        ctx.beginPath();
        ctx.moveTo(startPoint.x * width, startPoint.y * height);
        ctx.lineTo(endPoint.x * width, endPoint.y * height);
        ctx.stroke();
      }
    });
    
    // 주요 관절 점 (큰 빨간 원)
    const keyJoints = [11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
    ctx.fillStyle = '#ff0000';
    keyJoints.forEach((idx) => {
      const landmark = poseLandmarks[idx];
      if (landmark) {
        ctx.beginPath();
        ctx.arc(landmark.x * width, landmark.y * height, 8, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
    
    // 손가락/발가락 끝 (노란 원)
    const fingerTips = [19, 20, 31, 32];
    ctx.fillStyle = '#ffff00';
    fingerTips.forEach((idx) => {
      const landmark = poseLandmarks[idx];
      if (landmark) {
        ctx.beginPath();
        ctx.arc(landmark.x * width, landmark.y * height, 5, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
  }, []);

  // ✅ 지속적인 렌더링 루프 (MediaPipe와 독립적)
  useEffect(() => {
    if (!isStarted || isCompleted) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { width, height } = canvasDimensions.current;
    const ctx = canvas.getContext('2d');

    let isRunning = true;

    const renderLoop = () => {
      if (!isRunning) return;

      // 캔버스 클리어
      ctx.clearRect(0, 0, width, height);

      ctx.save();
      ctx.scale(-1, 1);
      ctx.translate(-width, 0);

      // ✅ 파란색 가이드 실루엣 그리기 (항상, ref 사용)
      if (showGuide && !isCompleted && guidePoses.length > 0) {
        const currentFrame = guideFrameRef.current; // ✅ ref에서 최신 값 가져오기
        if (currentFrame < guidePoses.length && guidePoses[currentFrame]) {
          drawGuideSilhouette(ctx, guidePoses[currentFrame], width, height);
        }
      }

      // ✅ 초록색 사용자 스켈레톤 그리기 (마지막 유효 포즈 사용)
      if (lastValidPose.current) {
        drawUserSkeleton(ctx, lastValidPose.current, width, height);
      }

      ctx.restore();

      // 다음 프레임 요청
      renderLoopRef.current = requestAnimationFrame(renderLoop);
    };

    // 렌더링 시작
    renderLoop();

    return () => {
      isRunning = false;
      if (renderLoopRef.current) {
        cancelAnimationFrame(renderLoopRef.current);
        renderLoopRef.current = null;
      }
    };
  }, [isStarted, isCompleted, showGuide, guidePoses, drawGuideSilhouette, drawUserSkeleton]); // ✅ guideFrame 제거

  // ✅ 자세 분석 결과 (정적/동적 운동 분기 처리)
  const onPoseResults = useCallback(async (results) => {
    const poseLandmarks = results.landmarks && results.landmarks.length > 0 
      ? results.landmarks[0] 
      : results.poseLandmarks;

    // ✅ 유효한 포즈가 감지되면 저장 (렌더링 루프에서 사용)
    if (poseLandmarks && poseLandmarks.length > 0) {
      lastValidPose.current = poseLandmarks;
      setPoseDetected(true);
    } else {
      setPoseDetected(false);
    }

    if (!poseLandmarks || isPaused || isCompleted) return;

    const now = Date.now();
    const timeSinceLastAnalysis = now - lastAnalysisTime.current;

    if (timeSinceLastAnalysis >= 2000 && exercise) {
      lastAnalysisTime.current = now;
      
      try {
        const landmarks = poseLandmarks.map(lm => ({
          x: lm.x, 
          y: lm.y, 
          z: lm.z || 0, 
          visibility: lm.visibility || 1.0
        }));
        
        const response = await exerciseAPI.analyzeRealtime(exerciseId, {
          pose_landmarks: landmarks,
          timestamp_ms: now
        });
        
        console.log('✅ API 응답:', response.data.score, response.data.feedback);
        
        setFeedback(response.data.feedback);
        const currentScore = response.data.score;
        setScore(currentScore);
        setTotalScore(prev => [...prev, currentScore]);
        
        // 백엔드에서 직접 카운팅한 경우
        if (response.data.new_rep_count !== undefined) {
          setCurrentRep(response.data.new_rep_count);
          console.log('🔢 백엔드 카운팅:', response.data.new_rep_count);
        }
        
        if (response.data.new_set_count !== undefined) {
          setCurrentSet(response.data.new_set_count);
          console.log('📦 백엔드 세트:', response.data.new_set_count);
        }
        
        // ✅ 운동 타입에 따른 분기 처리
        const isStatic = isStaticExercise(exercise.name);
        
        if (isStatic) {
          // ==========================================
          // ✅ 정적 운동 (자세 유지형) 카운팅 로직
          // ==========================================
          console.log('🧘 정적 운동 모드');
          
          // 자세가 정확하면 (점수 70 이상) 홀드 시작/유지
          if (currentScore >= 70) {
            if (!holdStartTime.current) {
              // 홀드 시작
              holdStartTime.current = now;
              currentHoldScore.current = [currentScore];
              console.log('⏱️ 홀드 시작!');
              setFeedback('좋습니다! 자세를 유지하세요...');
            } else {
              // 홀드 유지 중
              currentHoldScore.current.push(currentScore);
              const holdDuration = (now - holdStartTime.current) / 1000; // 초
              
              // ✅ repetitions를 "초" 단위로 해석 (예: 10회 = 10초)
              const requiredHoldTime = exercise.repetitions;
              
              console.log(`⏱️ 홀드 유지 중: ${holdDuration.toFixed(1)}초 / ${requiredHoldTime}초`);
              setFeedback(`자세 유지 중... ${holdDuration.toFixed(0)}초 / ${requiredHoldTime}초`);
              
              // ✅ 목표 시간 달성
              if (holdDuration >= requiredHoldTime && !repCooldown.current) {
                const avgHoldScore = Math.round(
                  currentHoldScore.current.reduce((a, b) => a + b, 0) / currentHoldScore.current.length
                );
                
                console.log('✅ 홀드 완료!', avgHoldScore, '점');
                
                // ✅ 직접 세트 증가 (정적 운동은 1회 유지 = 1세트)
                setCurrentSet(prevSet => {
                  const newSet = prevSet + 1;
                  
                  if (newSet > exercise.sets) {
                    console.log('🎉 모든 세트 완료!');
                    setIsCompleted(true);
                    setFeedback('모든 세트 완료! 수고하셨습니다!');
                    saveCompletion();
                    return exercise.sets;
                  } else {
                    console.log(`✅ ${prevSet}세트 완료! 다음 세트 ${newSet}/${exercise.sets}`);
                    setFeedback(`${prevSet}세트 완료! 잠시 쉬었다가 ${newSet}세트를 시작하세요.`);
                    return newSet;
                  }
                });
                
                // ✅ rep도 증가시켜서 UI에 표시
                setCurrentRep(prev => {
                  const newRep = prev + 1;
                  console.log('🎯 반복 횟수:', prev, '→', newRep);
                  return newRep;
                });
                
                // 홀드 초기화 및 쿨다운
                holdStartTime.current = null;
                currentHoldScore.current = [];
                repCooldown.current = true;
                setTimeout(() => {
                  repCooldown.current = false;
                  console.log('⏰ 쿨다운 해제 (다음 세트 준비)');
                }, 5000); // ✅ 5초 휴식 시간
              }
            }
          } else {
            // 자세가 부정확해지면 홀드 초기화
            if (holdStartTime.current) {
              console.log('❌ 자세 흐트러짐, 홀드 초기화');
              holdStartTime.current = null;
              currentHoldScore.current = [];
              setFeedback('자세가 흐트러졌습니다. 다시 정확한 자세를 유지하세요.');
            }
          }
          
        } else {
          // ==========================================
          // ✅ 동적 운동 (반복형) 카운팅 로직 (상태 플래그 사용)
          // ==========================================
          console.log('🏃 동적 운동 모드 - 현재 점수:', currentScore);
          
          if (response.data.new_rep_count === undefined && !repCooldown.current) {
            // ✅ 완화된 임계값 (더 쉽게 카운팅)
            const GOOD_POSE = 55;  // 60 → 55 (5점 낮춤)
            const TRANSITION_ZONE = 50; // 전환 구간 추가
            
            console.log(`📊 [점수] 현재: ${currentScore.toFixed(1)} | 기준: ${GOOD_POSE} | 동작 중: ${isInDownPhase.current}`);
            
            // ✅ 초기값 처리
            if (lastRepScore.current === 0) {
              console.log('🎬 첫 측정 - 초기값 설정');
              lastRepScore.current = currentScore;
              isInDownPhase.current = false;
              return;
            }
            
            // ✅ 상태 머신 (완화된 버전)
            
            // 1️⃣ 좋은 자세에서 점수가 떨어지기 시작 → 동작 시작
            if (!isInDownPhase.current && currentScore < GOOD_POSE) {
              console.log('📉 [시작] 동작 시작 감지! 점수 하락:', currentScore.toFixed(1));
              isInDownPhase.current = true; // 플래그 ON
            }
            
            // 2️⃣ 동작 중 상태에서 점수 추적
            if (isInDownPhase.current && currentScore < TRANSITION_ZONE) {
              console.log('💪 [진행] 동작 진행 중:', currentScore.toFixed(1));
            }
            
            // 3️⃣ 동작 중 상태에서 다시 좋은 자세로 복귀 → 동작 완료! ⭐
            // ✅ 완화: GOOD_POSE(55) 이상이면 카운팅 (이전: 60)
            if (isInDownPhase.current && currentScore >= GOOD_POSE) {
              console.log('✅ [완료] 동작 완료! 좋은 자세 복귀:', currentScore.toFixed(1));
              console.log('🎯 ===== 반복 카운팅! =====');
              
              isInDownPhase.current = false; // 플래그 OFF
              
              setCurrentRep(prevRep => {
                const newRep = prevRep + 1;
                console.log(`🔢 반복 횟수: ${prevRep} → ${newRep} / ${exercise.repetitions}`);
                
                if (newRep >= exercise.repetitions) {
                  console.log('🎉 ==== 세트 완료! ====');
                  setCurrentSet(prevSet => {
                    const newSet = prevSet + 1;
                    
                    if (newSet > exercise.sets) {
                      console.log('🏆 ===== 모든 세트 완료! =====');
                      setIsCompleted(true);
                      setFeedback('모든 세트 완료! 수고하셨습니다!');
                      saveCompletion();
                      return exercise.sets;
                    } else {
                      console.log(`✅ ${prevSet}세트 완료 → ${newSet}세트 시작`);
                      setFeedback(`${prevSet}세트 완료! 잠시 쉬었다가 ${newSet}세트를 시작하세요.`);
                      return newSet;
                    }
                  });
                  return 0;
                }
                
                setFeedback(`좋습니다! ${newRep}/${exercise.repetitions}회 완료`);
                return newRep;
              });
              
              // ✅ 쿨다운 단축: 1초 (이전: 1.5초)
              repCooldown.current = true;
              setTimeout(() => {
                repCooldown.current = false;
                console.log('⏰ 쿨다운 해제');
              }, 1000);
            }
            
            lastRepScore.current = currentScore;
            
          } else if (repCooldown.current) {
            console.log('⏸️ [대기] 쿨다운 중...');
          }
        }

      } catch (error) {
        console.error('❌ 자세 분석 실패:', error);
        setFeedback('서버 연결 실패');
      }
    }
  }, [isPaused, isCompleted, exercise, exerciseId, saveCompletion, isStaticExercise]);

  // MediaPipe 초기화 및 프레임 처리
  useEffect(() => {
    if (!exercise || !isStarted || isCompleted) return;

    console.log('🚀 MediaPipe 초기화 준비...');

    if (canvasRef.current) {
      canvasRef.current.width = canvasDimensions.current.width;
      canvasRef.current.height = canvasDimensions.current.height;
    }

    let isMounted = true;

    const initializePose = async () => {
      try {
        console.log('🔄 MediaPipe 초기화 시작...');
        
        if (poseRef.current) {
          try {
            poseRef.current.close();
          } catch (e) {
            console.log('이전 pose 인스턴스 정리:', e.message);
          }
          poseRef.current = null;
        }

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm"
        );
        console.log('✅ FilesetResolver 로드 완료');

        if (!isMounted) return;

        const poseLandmarker = await PoseLandmarker.createFromOptions(
          vision,
          {
            baseOptions: {
              modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
              delegate: "GPU"
            },
            runningMode: "VIDEO",
            numPoses: 1,
            minPoseDetectionConfidence: 0.5,
            minPosePresenceConfidence: 0.5,
            minTrackingConfidence: 0.5
          }
        );
        console.log('✅ PoseLandmarker 생성 완료');

        if (!isMounted) return;

        poseRef.current = poseLandmarker;
        setIsMediaPipeReady(true);
        setLoading(false);
        
        const video = webcamRef.current?.video;
        if (!video) {
          console.error('❌ Webcam video element not found');
          return;
        }

        const waitForVideo = () => {
          return new Promise((resolve) => {
            if (video.readyState >= 2) {
              resolve(true);
            } else {
              video.addEventListener('loadeddata', () => resolve(true), { once: true });
            }
          });
        };

        await waitForVideo();
        console.log('✅ Webcam video ready');

        const detectPose = async (now) => {
          if (!isMounted || !poseRef.current || isPaused || isCompleted) {
            return;
          }

          // ✅ 30fps로 감지 빈도 조정 (부드러움 개선)
          setTimeout(() => {
            animationFrameRef.current = requestAnimationFrame(detectPose);
          }, 1000 / 30); // 33ms 간격
          
          try {
            const currentTimestamp = Math.floor(now);
            
            if (currentTimestamp <= lastTimestampRef.current) {
              lastTimestampRef.current += 1;
            } else {
              lastTimestampRef.current = currentTimestamp;
            }
            
            if (video.readyState >= 2) {
              const results = poseRef.current.detectForVideo(video, lastTimestampRef.current);
              if (results && isMounted) {
                onPoseResults(results);
              }
            }
          } catch (err) {
            console.error('❌ Pose detect error:', err);
          }
        };

        detectPose(performance.now());
        console.log('✅ Pose detection loop started');

      } catch (error) {
        console.error('❌ MediaPipe 초기화 실패:', error);
        if (isMounted) {
          setError('자세 분석 모듈 로드 실패. 앱을 새로고침해주세요.');
          setLoading(false);
        }
      }
    };

    initializePose();

    return () => {
      console.log('🧹 MediaPipe cleanup...');
      isMounted = false;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      if (poseRef.current) {
        try {
          poseRef.current.close();
        } catch (e) {
          console.error('Pose close error:', e);
        }
        poseRef.current = null;
      }
      
      lastTimestampRef.current = -1;
      setIsMediaPipeReady(false);
    };
  }, [exercise, isStarted, isCompleted, isPaused, onPoseResults]);

  // 타이머
  useEffect(() => {
    if (!isStarted || isPaused || timeRemaining <= 0 || isCompleted) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsCompleted(true);
          saveCompletion();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isStarted, isPaused, timeRemaining, isCompleted, saveCompletion]);

  const handleComplete = () => {
    setIsCompleted(true);
    setIsStarted(false);
    saveCompletion();
  };

  const handleRestart = useCallback(() => {
    console.log('🔄 운동 재시작');
    
    // 1. 정리
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (renderLoopRef.current) {
      cancelAnimationFrame(renderLoopRef.current);
      renderLoopRef.current = null;
    }
    if (poseRef.current) {
      try { poseRef.current.close(); } catch (e) {}
      poseRef.current = null;
    }
    
    // 2. 상태 초기화
    setIsStarted(false);
    setIsPaused(false);
    setIsCompleted(false);
    setCurrentSet(1);
    setCurrentRep(0);
    setScore(100);
    setTotalScore([]);
    setFeedback('준비하세요');
    setTimeRemaining(exercise?.duration_seconds || 0);
    setShowGuide(true);
    setGuideFrame(0);
    setPoseDetected(false);
    
    // 3. ref 초기화
    lastAnalysisTime.current = 0;
    lastTimestampRef.current = -1;
    lastRepScore.current = 0;
    repCooldown.current = false;
    isInDownPhase.current = false;
    lastValidPose.current = null;
    guideFrameRef.current = 0;
    
    console.log('✅ 재시작 완료');
  }, [exercise]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#4CAF50] mb-4"></div>
        <div className="text-white text-2xl">운동 정보 로딩 중...</div>
        {isStarted && (
          <div className="text-gray-400 mt-2">AI 모듈 초기화 중...</div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <div className="text-white text-2xl mb-2">로딩 실패</div>
        <div className="text-gray-400 text-center max-w-md">{error}</div>
        <div className="flex gap-4 mt-6">
          <button
            onClick={() => window.location.reload()}
            className="bg-[#4CAF50] hover:bg-blue-600 text-white px-3 py-3 rounded-lg"
          >
            다시 시도
          </button>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-500 hover:bg-gray-600 text-gray-900 px-3 py-3 rounded-lg"
          >
            홈으로
          </button>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-white text-2xl">운동 정보를 찾을 수 없습니다</div>
        <button
          onClick={() => navigate('/')}
          className="bg-gray-500 hover:bg-gray-600 text-gray-900 px-3 py-3 rounded-lg ml-4"
        >
          홈으로
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#ccf4d3] to-white text-gray-900 p-4">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 bg-[#4CAF50] bg-opacity-80 hover:bg-opacity-100 px-4 py-2 rounded-lg transition backdrop-blur-sm mb-2 text-white"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>나가기</span>
      </button>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="relative bg-white rounded-lg overflow-hidden" style={{ height: '70vh' }}>
            <Webcam
              ref={webcamRef}
              className="absolute top-0 left-0 w-full h-full object-cover"
              mirrored={true}
              videoConstraints={{ 
                width: canvasDimensions.current.width, 
                height: canvasDimensions.current.height,
                facingMode: "user"
              }}
            />
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full"
              width={canvasDimensions.current.width}
              height={canvasDimensions.current.height}
            />
            
            <div className="absolute top-4 left-4 bg-white bg-opacity-70 px-2  py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${poseDetected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div className="text-sm">{poseDetected ? '감지됨' : '사람 없음'}</div>
              </div>
              {/* ✅ 디버깅: 점수 변화 표시 */}
              {!isCompleted && isStarted && (
                <div className="text-xs text-black mt-1">
                  이전: {lastRepScore.current.toFixed(0)} / 기준: 55
                </div>
              )}
            </div>

            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-70 px-4 py-2 rounded-lg">
              <div className="text-4xl font-bold">{score}</div>
              <div className="text-sm text-black text-center">점수</div>
            </div>

            <div className="absolute top-4 right-4 bg-white bg-opacity-70 px-3 py-2 rounded-lg">
              <div className="text-2xl font-mono">
                {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
              </div>
            </div>

            {!isCompleted && (
              <>
                <button
                  onClick={() => setShowGuide(!showGuide)}
                  className="absolute top-20 right-4 bg-white bg-opacity-70 p-3 rounded-lg hover:bg-opacity-90 transition z-10"
                >
                  {showGuide ? <Eye className="w-6 h-6" /> : <EyeOff className="w-6 h-6" />}
                </button>

                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 px-2 py-4 rounded-lg flex gap-4 z-10" style={{ height: '300px' }}>
                  <div className="flex flex-col items-center">
                    <div className="text-xs text-black mb-2">세트</div>
                    <div className="flex-1 w-4 bg-gray-400 rounded-full relative flex flex-col-reverse">
                      <div
                        className="bg-[#4CAF50] rounded-full transition-all w-full"
                        style={{ height: `${exercise && exercise.sets > 0 ? Math.min(((currentSet - 1) / exercise.sets) * 100, 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-900 font-semibold mt-2">{currentSet}/{exercise?.sets || 0}</div>
                  </div>
              
                  <div className="flex flex-col items-center">
                    <div className="text-xs text-black mb-2">반복</div>
                    <div className="flex-1 w-4 bg-gray-400 rounded-full relative flex flex-col-reverse">
                      <div
                        className="bg-green-500 rounded-full transition-all w-full"
                        style={{ height: `${exercise && exercise.repetitions > 0 ? Math.min((currentRep / exercise.repetitions) * 100, 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-black font-semibold mt-2">{currentRep}/{exercise?.repetitions || 0}</div>
                  </div>
                </div>
              </>
            )}

            {isCompleted && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex flex-col items-center justify-center z-50 p-4 space-y-4 overflow-y-auto">
                <h2 className="text-4xl font-bold text-black mb-1">운동 완료!</h2>
                <p className="text-xl text-gray-600 mb-4">
                  {exercise.sets}세트 × {exercise.repetitions}회 달성
                </p>
                
                {/* ✅ 저장 상태 표시 */}
                {isSavingCompletion && (
                  <div className="bg-blue-900 bg-opacity-50 border border-blue-500 rounded-lg p-4 w-full max-w-lg">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400"></div>
                      <p className="text-blue-300">기록 저장 중...</p>
                    </div>
                  </div>
                )}
                
                {/* ✅ 저장 에러 표시 */}
                {saveError && (
                  <div className="bg-red-900 bg-opacity-50 border border-red-500 rounded-lg p-4 w-full max-w-lg">
                    <h4 className="font-semibold text-red-400 mb-2">❌ 기록 저장 실패</h4>
                    <p className="text-red-300 text-sm mb-3">{saveError}</p>
                    <button
                      onClick={() => {
                        setSaveError(null);
                        saveCompletion();
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
                    >
                      다시 시도
                    </button>
                  </div>
                )}
                
                {/* ✅ 저장 성공 표시 */}
                {!isSavingCompletion && !saveError && (
                  <div className="bg-green-900 bg-opacity-50 border border-green-500 rounded-lg p-3 w-full max-w-lg">
                    <p className="text-green-300 text-center">✅ 기록이 저장되었습니다</p>
                  </div>
                )}
                
                <div className="bg-white border border-gray-300 rounded-lg p-5 w-full max-w-lg">
                  <div className="text-3xl font-bold text-blue-400 mb-2">
                    {totalScore.length > 0 
                      ? Math.round(totalScore.reduce((a, b) => a + b, 0) / totalScore.length)
                      : 0}점
                  </div>
                  <p className="text-gray-400">평균 점수</p>
                  <p className="text-xs text-gray-500 mt-2">
                    완료 세트: {currentSet} | 총 측정: {totalScore.length}회
                  </p>
                </div>

                {completionFeedback && (
                  <div className="bg-gray-800 rounded-lg p-6 w-full max-w-lg text-left space-y-4">
                    <h3 className="text-xl font-semibold text-white mb-3 text-center">
                      AI 종합 피드백
                    </h3>
                    
                    {completionFeedback.summary && (
                      <div>
                        <h4 className="font-semibold text-blue-400 mb-1">종합 평가</h4>
                        <p className="text-gray-300 whitespace-pre-line">
                          {completionFeedback.summary}
                        </p>
                      </div>
                    )}

                    {completionFeedback.strengths && completionFeedback.strengths.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-green-400 mb-1">👍 잘한 점</h4>
                        <ul className="list-disc list-inside text-gray-300 space-y-1">
                          {completionFeedback.strengths.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {completionFeedback.improvements && completionFeedback.improvements.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-yellow-400 mb-1">✏️ 개선할 점</h4>
                        <ul className="list-disc list-inside text-gray-300 space-y-1">
                          {completionFeedback.improvements.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-4 w-full max-w-lg pt-3">
                  <button
                    onClick={handleRestart}
                    className="flex-1 px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg text-lg font-semibold transition"
                  >
                    다시 하기
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 px-8 py-4 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-lg font-semibold transition"
                  >
                    홈으로
                  </button>
                </div>
              </div>
            )}

            <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-70 px-6 py-3 rounded-lg">
              <p className="text-lg text-center">{feedback}</p>
              {showGuide && !isCompleted && guidePoses.length > 0 && (
                <p className="text-sm text-blue-400 text-center mt-1">
                  파란색 가이드를 따라하세요
                </p>
              )}
              {!poseDetected && isStarted && !isCompleted && (
                <p className="text-sm text-yellow-400 text-center mt-1">
                  💡 카메라 앞에 서서 전신이 보이도록 해주세요
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-4 mt-4">
            {!isStarted ? (
              <button
                onClick={() => setIsStarted(true)}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-4 rounded-lg text-lg font-semibold transition"
              >
                운동 시작
              </button>
            ) : isCompleted ? (
              <div className="flex-1 flex gap-4">
                <button
                  onClick={handleRestart}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-4 rounded-lg text-lg font-semibold transition"
                >
                  다시 하기
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 bg-gray-500 hover:bg-gray-400 text-white rounded-lg text-lg font-semibold transition"
                >
                  종료
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setIsPaused(!isPaused)}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-4 rounded-lg text-lg font-semibold transition"
                >
                  {isPaused ? '재개' : '일시정지'}
                </button>
                <button
                  onClick={handleComplete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-4 rounded-lg text-lg font-semibold transition"
                >
                  종료
                </button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6 border shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-black">운동 지침</h3>
            <ol className="space-y-2 text-sm text-black">
              {exercise.instructions.map((instruction, index) => (
                <li key={index} className="flex">
                  <span className="font-semibold mr-2">{index + 1}.</span>
                  <span>{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 shadow-xl">
            <div className='flex gap-2'>
              <AlertTriangle className="w-5 h-5 text-red-600 mt-1.5" />
              <h3 className="text-xl font-bold mb-4 text-red-600">주의사항</h3>
            </div>
            
            <ul className="space-y-2 text-sm text-red-800">
              {exercise.safety_warnings.map((warning, index) => (
                <li key={index}>• {warning}</li>
              ))}
            </ul>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default ExercisePage;